import { useEffect, useState, useMemo, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';

import type { Company, Quotation } from '../types';
import DonutChart from '../components/charts/DonutChart';
import BarChart from '../components/charts/BarChart';
import MiniTrend from '../components/charts/MiniTrend';
import ActivityTimeline from '../components/charts/ActivityTimeline';

interface DashboardProps {
  onNavigate?: (page: string) => void;
}

export default function Dashboard({ onNavigate }: DashboardProps) {
  const { user, profile } = useAuth();
  const { companies, selectedCompanyId, selectedCompany, updateCompany } = useCompany();
  const [partyCount, setPartyCount] = useState(0);

  const [allQuotes, setAllQuotes] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);

  // Edit Company Modal
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [companySaving, setCompanySaving] = useState(false);
  const [companySuccessMsg, setCompanySuccessMsg] = useState<string | null>(null);
  const [companyErrorMsg, setCompanyErrorMsg] = useState<string | null>(null);
  const [companyForm, setCompanyForm] = useState({
    code: '', name: '', gst: '', address: '', phone: '', email: '',
    bank_name: '', account_number: '', ifsc_code: '', signatory: '',
  });

  useEffect(() => { loadDashboardData(); }, [user]);

  async function loadDashboardData() {
    try {
      setLoading(true);
      const [partiesRes, quotesRes] = await Promise.all([
        supabase.from('parties').select('id', { count: 'exact' }).limit(1),
        supabase.from('quotations')
          .select('*, party:parties(*), company:companies(*), items:quotation_items(*)')
          .order('quotation_date', { ascending: false }),
      ]);
      if (partiesRes.count != null) setPartyCount(partiesRes.count);

      if (quotesRes.data) setAllQuotes(quotesRes.data);
    } catch (err) {
      console.warn('Dashboard data fetch warning:', err);
    } finally { setLoading(false); }
  }

  // ─── Derived Data ───
  const filteredQuotes = selectedCompanyId === 'all'
    ? allQuotes
    : allQuotes.filter(q => q.company_id === selectedCompanyId);

  const totalRevenue = useMemo(() =>
    filteredQuotes.reduce((sum, q) => sum + (q.total || 0), 0), [filteredQuotes]);

  const avgQuoteValue = useMemo(() =>
    filteredQuotes.length > 0 ? Math.round(totalRevenue / filteredQuotes.length) : 0, [totalRevenue, filteredQuotes]);

  // Status distribution for donut chart
  const statusData = useMemo(() => {
    const counts = { draft: 0, sent: 0, approved: 0, rejected: 0 };
    filteredQuotes.forEach(q => {
      const s = (q.status || 'draft').toLowerCase();
      if (s in counts) counts[s as keyof typeof counts]++;
    });
    return [
      { label: 'Approved', value: counts.approved, color: '#10b981' },
      { label: 'Sent', value: counts.sent, color: '#3b82f6' },
      { label: 'Draft', value: counts.draft, color: '#f59e0b' },
      { label: 'Rejected', value: counts.rejected, color: '#ef4444' },
    ].filter(d => d.value > 0);
  }, [filteredQuotes]);

  // Monthly data for bar chart (last 6 months)
  const monthlyData = useMemo(() => {
    const months: Record<string, number> = {};
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = d.toLocaleString('en-IN', { month: 'short' });
      months[key] = 0;
    }
    filteredQuotes.forEach(q => {
      if (q.quotation_date) {
        const d = new Date(q.quotation_date);
        const key = d.toLocaleString('en-IN', { month: 'short' });
        if (key in months) months[key] += q.total || 0;
      }
    });
    return Object.entries(months).map(([label, value]) => ({ label, value: Math.round(value) }));
  }, [filteredQuotes]);

  // Revenue trend for sparkline (monthly last 6 months)
  const revenueTrend = useMemo(() => monthlyData.map(d => d.value), [monthlyData]);

  // Top 5 customers by quotation value
  const topCustomers = useMemo(() => {
    const customerMap: Record<string, { name: string; total: number; count: number }> = {};
    filteredQuotes.forEach(q => {
      const name = q.party?.name || 'Unknown';
      if (!customerMap[name]) customerMap[name] = { name, total: 0, count: 0 };
      customerMap[name].total += q.total || 0;
      customerMap[name].count++;
    });
    return Object.values(customerMap)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [filteredQuotes]);

  // Activity timeline from recent quotes
  const activityEvents = useMemo(() => {
    return filteredQuotes.slice(0, 8).map((q, i) => ({
      id: `q-${q.id || i}`,
      type: 'quotation' as const,
      title: `Quotation ${q.quotation_number}`,
      detail: `${q.party?.name || 'Customer'} — ${q.company?.code || ''}${q.query_date ? ` • Qry: ${q.query_date}` : ''}`,
      timestamp: q.created_at || q.quotation_date || new Date().toISOString(),
      amount: q.total || 0,
    }));
  }, [filteredQuotes]);



  function openEditCompany(c: Company) {
    setEditingCompany(c);
    setCompanyForm({
      code: c.code || '', name: c.name || '', gst: c.gst || '',
      address: c.address || '', phone: c.phone || '', email: c.email || '',
      bank_name: c.bank_name || '', account_number: c.account_number || '',
      ifsc_code: c.ifsc_code || '', signatory: c.signatory || '',
    });
    setCompanyErrorMsg(null);
    setShowEditCompanyModal(true);
  }

  async function handleSaveCompany(e: FormEvent) {
    e.preventDefault();
    if (!editingCompany?.id) return;
    if (!companyForm.name.trim() || !companyForm.code.trim()) {
      setCompanyErrorMsg('Company Code and Name are required.');
      return;
    }
    setCompanySaving(true);
    setCompanyErrorMsg(null);
    try {
      const payload = {
        code: companyForm.code.trim().toUpperCase(),
        name: companyForm.name.trim().toUpperCase(),
        gst: companyForm.gst.trim() || null, address: companyForm.address.trim() || null,
        phone: companyForm.phone.trim() || null, email: companyForm.email.trim() || null,
        bank_name: companyForm.bank_name.trim() || null,
        account_number: companyForm.account_number.trim() || null,
        ifsc_code: companyForm.ifsc_code.trim() || null,
        signatory: companyForm.signatory.trim() || null,
      };
      const ok = await updateCompany(editingCompany.id, payload);
      if (!ok) throw new Error('Could not update company details');
      setCompanySuccessMsg(`Company "${payload.name}" updated successfully.`);
      setShowEditCompanyModal(false);
      setTimeout(() => setCompanySuccessMsg(null), 5000);
    } catch (err: any) {
      setCompanyErrorMsg(err.message || 'Failed to update company');
    } finally { setCompanySaving(false); }
  }

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  const todayStr = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6">
      {/* ═══ Welcome Banner ═══ */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="text-xs text-gray-400 font-medium mb-1">{todayStr}</div>
          <h1 className="text-xl font-bold text-gray-900">
            {getGreeting()}, {profile?.name || user?.email?.split('@')[0] || 'Executive'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Monitor branch performance, compute pricing, and manage documents.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => onNavigate?.('quotations')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer">
            <span>+</span><span>New Quotation</span>
          </button>
          <button onClick={() => onNavigate?.('companies')}
            className="bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs px-4 py-2 rounded-lg border border-gray-300 transition-colors cursor-pointer">
            Manage Companies
          </button>
        </div>
      </div>

      {companySuccessMsg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl text-xs font-bold flex items-center justify-between">
          <div className="flex items-center gap-2"><span>✓</span><span>{companySuccessMsg}</span></div>
          <button onClick={() => setCompanySuccessMsg(null)} className="text-emerald-600 hover:text-emerald-900">✕</button>
        </div>
      )}

      {/* ═══ KPI Stats Row ═══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue" value={`₹${totalRevenue.toLocaleString('en-IN')}`}
          subtext={`${filteredQuotes.length} quotations`} icon="💰" badge="Live"
          trend={revenueTrend} trendColor="#10b981"
          onClick={() => onNavigate?.('quotations')}
        />
        <StatCard
          title="Customers" value={partyCount.toLocaleString()}
          subtext="Verified corporate accounts" icon="👥" badge="Clean"
          onClick={() => onNavigate?.('parties')}
        />
        <StatCard
          title="Entities" value={selectedCompany ? '1' : companies.length.toString()}
          subtext={selectedCompany ? selectedCompany.code : 'Operating branches'} icon="🏢" badge="Verified"
          onClick={() => onNavigate?.('companies')}
        />
        <StatCard
          title="Avg. Quote" value={`₹${avgQuoteValue.toLocaleString('en-IN')}`}
          subtext={`Per quotation average`} icon="📊" badge="Metric"
          onClick={() => onNavigate?.('reports')}
        />
      </div>

      {/* ═══ Charts Row: Donut + Bar ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Donut */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Quotation Status</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">Distribution by current status</p>
            </div>
            <button onClick={() => onNavigate?.('reports')} className="text-[10px] font-bold text-blue-600 hover:text-blue-700">View All →</button>
          </div>
          <DonutChart
            data={statusData}
            centerValue={filteredQuotes.length.toString()}
            centerLabel="Total"
          />
        </div>

        {/* Monthly Revenue Bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Monthly Revenue</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">Last 6 months quotation value</p>
            </div>
            <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-lg">
              Total: ₹{totalRevenue.toLocaleString('en-IN')}
            </span>
          </div>
          <BarChart
            data={monthlyData}
            height={200}
            formatValue={(v) => `₹${(v / 1000).toFixed(0)}K`}
          />
        </div>
      </div>

      {/* ═══ Main Grid: Activity + Top Customers + Quick Actions ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Timeline (2 cols) */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6">
          <div className="flex justify-between items-center mb-5">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Recent Activity</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">Latest quotation events across branches</p>
            </div>
            <button onClick={() => onNavigate?.('quotations')} className="text-xs font-bold text-blue-600 hover:text-blue-700">
              View All ({filteredQuotes.length}) →
            </button>
          </div>
          {loading ? (
            <div className="py-12 flex justify-center items-center">
              <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : (
            <ActivityTimeline events={activityEvents} />
          )}
        </div>

        {/* Right Column: Top Customers + Quick Actions */}
        <div className="space-y-6">
          {/* Top Customers */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-sm font-bold text-slate-900">Top Customers</h2>
              <button onClick={() => onNavigate?.('parties')} className="text-[10px] font-bold text-blue-600">Browse →</button>
            </div>
            {topCustomers.length > 0 ? (
              <div className="space-y-3">
                {topCustomers.map((c, i) => (
                  <div key={c.name} className="flex items-center gap-3 group">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 ${
                      i === 0 ? 'bg-amber-100 text-amber-700' :
                      i === 1 ? 'bg-slate-100 text-slate-600' :
                      i === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-blue-50 text-blue-600'
                    }`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-900 truncate group-hover:text-blue-700 transition-colors">{c.name}</p>
                      <p className="text-[10px] text-slate-500">{c.count} quotation{c.count > 1 ? 's' : ''}</p>
                    </div>
                    <span className="text-xs font-black text-slate-900">₹{(c.total / 1000).toFixed(0)}K</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-4">No customer data yet</p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 space-y-3">
            <h2 className="text-sm font-bold text-slate-900 mb-3">Quick Actions</h2>
            {[
              { icon: '⚡', label: 'Quotations Engine', desc: 'Create & manage quotes', nav: 'quotations', color: 'bg-blue-50 text-blue-600' },
              { icon: '🏗️', label: 'Lattice Calculator', desc: 'Conveyor pricing tool', nav: 'lattice', color: 'bg-emerald-50 text-emerald-600' },
              { icon: '📥', label: 'Bulk Import', desc: 'Upload Excel data', nav: 'import', color: 'bg-amber-50 text-amber-600' },
              { icon: '📈', label: 'Reports & Analytics', desc: 'Branch performance', nav: 'reports', color: 'bg-purple-50 text-purple-600' },
            ].map(action => (
              <button key={action.nav} onClick={() => onNavigate?.(action.nav)}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all group cursor-pointer text-left">
                <div className={`h-9 w-9 rounded-xl ${action.color} flex items-center justify-center text-lg group-hover:scale-110 transition-transform`}>
                  {action.icon}
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900 group-hover:text-blue-700 transition-colors">{action.label}</p>
                  <p className="text-[10px] text-slate-500">{action.desc}</p>
                </div>
                <span className="ml-auto text-slate-300 group-hover:text-blue-500 text-xs transition-colors">→</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ Branch Performance Row ═══ */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6">
        <div className="flex justify-between items-center mb-5">
          <div>
            <h2 className="text-sm font-bold text-slate-900">Branch Performance</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">Quotation count and value per entity</p>
          </div>
          <button onClick={() => onNavigate?.('companies')} className="text-xs font-bold text-blue-600 hover:text-blue-700">Manage All →</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {companies.slice(0, 6).map(c => {
            const branchQuotes = allQuotes.filter(q => q.company_id === c.id);
            const branchRevenue = branchQuotes.reduce((s, q) => s + (q.total || 0), 0);
            return (
              <button key={c.id} onClick={() => openEditCompany(c)}
                className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 hover:border-blue-300 hover:shadow-md transition-all text-left group cursor-pointer">
                <div className="font-mono text-[10px] font-extrabold px-2 py-0.5 rounded bg-blue-100 text-blue-800 inline-block mb-2 group-hover:bg-blue-200 transition-colors">
                  {c.code}
                </div>
                <p className="text-[10px] font-bold text-slate-700 truncate">{c.name}</p>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-[9px] text-slate-500">Quotes</span>
                    <span className="text-[10px] font-black text-slate-900">{branchQuotes.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[9px] text-slate-500">Revenue</span>
                    <span className="text-[10px] font-black text-slate-900">₹{(branchRevenue / 1000).toFixed(0)}K</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ Company Edit Modal ═══ */}
      {showEditCompanyModal && editingCompany && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-xl w-full p-6 space-y-5 shadow-lg border border-gray-200 my-8">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Update Branch</span>
                <h3 className="text-lg font-bold text-slate-900">Edit Company ({editingCompany.code})</h3>
              </div>
              <button onClick={() => setShowEditCompanyModal(false)} className="text-slate-400 hover:text-slate-600 font-bold text-lg p-1 rounded-lg hover:bg-slate-100 cursor-pointer">✕</button>
            </div>
            {companyErrorMsg && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs font-bold">{companyErrorMsg}</div>}
            <form onSubmit={handleSaveCompany} className="space-y-4 text-xs">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="block font-bold text-slate-700 mb-1">Code *</label>
                  <input required value={companyForm.code} onChange={e => setCompanyForm({ ...companyForm, code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono font-bold focus:border-blue-500" />
                </div>
                <div className="col-span-2">
                  <label className="block font-bold text-slate-700 mb-1">Name *</label>
                  <input required value={companyForm.name} onChange={e => setCompanyForm({ ...companyForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-bold focus:border-blue-500" />
                </div>
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">GSTIN</label>
                <input value={companyForm.gst} onChange={e => setCompanyForm({ ...companyForm, gst: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono focus:border-blue-500" />
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Address</label>
                <textarea rows={2} value={companyForm.address} onChange={e => setCompanyForm({ ...companyForm, address: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Phone</label>
                  <input value={companyForm.phone} onChange={e => setCompanyForm({ ...companyForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono focus:border-blue-500" />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Email</label>
                  <input type="email" value={companyForm.email} onChange={e => setCompanyForm({ ...companyForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500" />
                </div>
              </div>
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                <div className="font-bold text-slate-900 text-xs flex items-center gap-1.5"><span>🏦</span><span>Bank Details</span></div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Bank</label>
                    <input value={companyForm.bank_name} onChange={e => setCompanyForm({ ...companyForm, bank_name: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 bg-white" />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">A/C No</label>
                    <input value={companyForm.account_number} onChange={e => setCompanyForm({ ...companyForm, account_number: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono focus:border-blue-500 bg-white" />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">IFSC</label>
                    <input value={companyForm.ifsc_code} onChange={e => setCompanyForm({ ...companyForm, ifsc_code: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono focus:border-blue-500 bg-white" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Signatory</label>
                <input value={companyForm.signatory} onChange={e => setCompanyForm({ ...companyForm, signatory: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setShowEditCompanyModal(false)}
                  className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
                <button type="submit" disabled={companySaving}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-600/20 disabled:opacity-50 transition-colors cursor-pointer">
                  {companySaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Stat Card with Mini Trend ─── */
function StatCard({ title, value, subtext, icon, badge, trend, trendColor, onClick }: {
  title: string; value: string; subtext: string; icon: string; badge: string;
  trend?: number[]; trendColor?: string; onClick?: () => void;
}) {
  return (
    <div onClick={onClick}
      className={`bg-white rounded-2xl p-5 shadow-sm border border-slate-200/80 transition-all space-y-3 ${
        onClick ? 'cursor-pointer hover:border-blue-500 hover:shadow-sm hover:-translate-y-0.5 group' : ''
      }`}>
      <div className="flex justify-between items-start">
        <span className="text-2xl group-hover:scale-110 transition-transform">{icon}</span>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 group-hover:bg-blue-50 group-hover:text-blue-700 group-hover:border-blue-200 transition-colors">
          {badge}
        </span>
      </div>
      <div>
        <div className="flex items-center gap-2">
          <div className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight group-hover:text-blue-600 transition-colors">
            {value}
          </div>
          {trend && trend.length >= 2 && (
            <MiniTrend data={trend} color={trendColor || '#3b82f6'} />
          )}
        </div>
        <div className="text-xs font-bold text-slate-700 mt-0.5">{title}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">{subtext}</div>
      </div>
    </div>
  );
}
